import { useEffect, useState } from 'react';
import jwtDecode from 'jwt-decode';
import { calculationRunSnapshot } from './project/lcaRunMonitor';
import { hostname } from '../providers/api';

const activeStatuses = new Set(['QUEUED', 'CALCULATING', 'PROCESSING']);
const terminal = (run) => ['succeeded', 'failed'].includes(run?.runStatus);
export function pollCalculationLogProgress({
  records,
  request = fetch,
  getToken,
  onChange,
  baseUrl = hostname,
}) {
  let disposed = false,
    timer,
    controller,
    requestTimer;
  const progress = {};
  const visible = records.slice(0, 100);
  const poll = async (initial = false) => {
    const ids = visible
      .filter(
        (row) =>
          initial ||
          (!terminal(progress[row.id]) && activeStatuses.has(row.status))
      )
      .map((row) => String(row.id));
    if (disposed || !ids.length) return;
    try {
      const token = getToken();
      if (!token) throw new Error('Account token unavailable');
      controller = new AbortController();
      requestTimer = setTimeout(() => controller.abort(), 30000);
      const response = await request(
        `${baseUrl}/calculationLog/run-progress?ids=${encodeURIComponent(
          ids.join(',')
        )}`,
        {
          headers: { authorization: `Bearer ${token}` },
          signal: controller.signal,
        }
      );
      if (!response.ok) throw new Error('Status unavailable');
      const data = await response.json();
      if (disposed) return;
      for (const id of ids) {
        const run = data?.runs?.[id];
        if (run) progress[id] = calculationRunSnapshot(run);
        else if (progress[id]) progress[id] = { ...progress[id], stale: true };
      }
      onChange({ ...progress });
    } catch {
      if (disposed) return;
      for (const id of ids)
        if (progress[id]) progress[id] = { ...progress[id], stale: true };
      onChange({ ...progress });
    } finally {
      clearTimeout(requestTimer);
    }
    if (!disposed) timer = setTimeout(() => poll(), 3000);
  };
  poll(true);
  return () => {
    disposed = true;
    clearTimeout(timer);
    clearTimeout(requestTimer);
    controller?.abort();
  };
}

export function useCalculationLogProgress(records, accountId) {
  const recordsKey = JSON.stringify(
    records.map(({ id, status }) => ({ id, status }))
  );
  const scope = `${accountId}:${recordsKey}`;
  const [state, setState] = useState({ scope: null, progress: {} });
  useEffect(() => {
    if (!accountId) return;
    return pollCalculationLogProgress({
      records: JSON.parse(recordsKey),
      onChange: (progress) => setState({ scope, progress }),
      getToken: () => {
        try {
          const token = JSON.parse(localStorage.getItem('bcome-admin-auth'))
            ?.accountToken?.accessToken;
          return token &&
            String(jwtDecode(token).accountId) === String(accountId)
            ? token
            : null;
        } catch {
          return null;
        }
      },
    });
  }, [scope, recordsKey, accountId]);
  return state.scope === scope ? state.progress : {};
}
